import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { TfiCreditCard } from "react-icons/tfi";
import { GoTrash } from "react-icons/go";
import { BsToggleOn, BsToggleOff } from "react-icons/bs";
import api from '../../../../hooks/api';
import { BankCardRecognizer } from '../../../../hooks/BankCardRecognizer';
import './PaymentGateway.css';
import { isValidCardNumber, normalizeCardNumber } from './validateCardNumber';

// مسیر تصاویر درگاه‌ها از پوشه public
const zibalIcon = '/images/bankIcons/zibal_icon.png';
const zarinpalIcon = '/images/bankIcons/zarinpal_icon.png';

function PaymentGateway({ businessHook }) {
    const {
        businessId,
        data: businessData
    } = businessHook || {};

    // --- State های زرین‌پال ---
    const [merchantId, setMerchantId] = useState("");
    const [isZarinpalActive, setIsZarinpalActive] = useState(false);

    // --- State های زیبال ---
    const [zibalMerchantId, setZibalMerchantId] = useState("");
    const [isZibalActive, setIsZibalActive] = useState(false);

    // --- State های کارت به کارت ---
    const [cardNumberInput, setCardNumberInput] = useState("");
    const [cards, setCards] = useState([]);
    const [isCardToCardActive, setIsCardToCardActive] = useState(false);

    const [disable, setDisable] = useState(false);

    const fetchedIdRef = useRef(null);

    // دریافت اطلاعات اولیه از بیزینس
    useEffect(() => {
        if (businessData) {
            if (businessData.merchant_id) {
                setMerchantId(businessData.merchant_id);
            }
            if (businessData.active_merchantId) {
                setIsZarinpalActive(businessData.active_merchantId == "1");
            }
        }
    }, [businessData]);

    // دریافت اطلاعات یکپارچه درگاه‌ها از API
    useEffect(() => {
        if (businessId && fetchedIdRef.current !== businessId) {

            fetchedIdRef.current = businessId;

            api.post('business/payment/gateway/show', { business_id: businessId })
                .then((response) => {
                    if (response.data && response.data.success) {
                        const gateways = response.data.data;

                        if (Array.isArray(gateways)) {
                            // --- تنظیمات زرین پال (transactionType_id: 2) ---
                            const zarinpalGateway = gateways.find(g => g.transactionType_id == 2);
                            if (zarinpalGateway) {
                                setIsZarinpalActive(zarinpalGateway.isActive);
                                if (zarinpalGateway.config) {
                                    let configObj = zarinpalGateway.config;
                                    if (typeof configObj === 'string') {
                                        try { configObj = JSON.parse(configObj); } catch (error) { configObj = {}; }
                                    }
                                    if (configObj && configObj.merchant_id) {
                                        setMerchantId(configObj.merchant_id);
                                    }
                                }
                            }

                            // --- تنظیمات زیبال (transactionType_id: 3) ---
                            const zibalGateway = gateways.find(g => g.transactionType_id == 3);
                            if (zibalGateway) {
                                setIsZibalActive(zibalGateway.isActive);
                                if (zibalGateway.config) {
                                    let configObj = zibalGateway.config;
                                    if (typeof configObj === 'string') {
                                        try { configObj = JSON.parse(configObj); } catch (error) { configObj = {}; }
                                    }
                                    if (configObj && configObj.merchant_id) {
                                        setZibalMerchantId(configObj.merchant_id);
                                    }
                                }
                            }

                            // --- تنظیمات کارت به کارت (transactionType_id: 1) ---
                            const cardToCardGateway = gateways.find(g => g.transactionType_id == 1);
                            if (cardToCardGateway) {
                                setIsCardToCardActive(cardToCardGateway.isActive);
                                if (cardToCardGateway.config) {
                                    let configObj = cardToCardGateway.config;
                                    if (typeof configObj === 'string') {
                                        try { configObj = JSON.parse(configObj); } catch (error) { configObj = []; }
                                    }
                                    setCards(Array.isArray(configObj) ? configObj : []);
                                }
                            }
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error fetching gateway data:", error);
                });
        }
    }, [businessId]);

    // ثبت اطلاعات درگاه (زرین پال یا زیبال)
    const handleSaveMerchant = (transactionType_id, merchantValue, gatewayName) => {
        if (!merchantValue) {
            toast.error("لطفا کد مرچنت را وارد کنید");
            return;
        }
        setDisable(true);
        api.post(`business/payment/gateway/store`, {
            business_id: businessId,
            transactionType_id: transactionType_id,
            config: JSON.stringify({ merchant_id: merchantValue })
        })
            .then((response) => {
                setDisable(false);
                if (response.data.success) toast.success(`کد مرچنت ${gatewayName} ذخیره شد`);
                else toast.error(response.data.message || "خطا");
            })
            .catch(() => {
                setDisable(false);
                toast.error("خطا در برقراری ارتباط");
            });
    };

    const toggleZarinpal = () => {
        if (disable) return;
        const newState = !isZarinpalActive;
        setIsZarinpalActive(newState);

        api.post(`business/payment/gateway/activation`, {
            id_business: businessId,
            isActive: newState,
            transactionType_id: 2
        })
            .then((response) => {
                if (!response.data.success) {
                    setIsZarinpalActive(!newState);
                    toast.error(response.data.message || "خطا در تغییر وضعیت");
                } else {
                    toast.success(newState ? "درگاه زرین‌پال فعال شد" : "درگاه زرین‌پال غیرفعال شد");
                }
            })
            .catch((err) => {
                setIsZarinpalActive(!newState);
                console.error(err);
                toast.error("خطا در برقراری ارتباط با سرور");
            });
    };

    const toggleZibal = () => {
        if (disable) return;
        const newState = !isZibalActive;
        setIsZibalActive(newState);

        api.post(`business/payment/gateway/activation`, {
            id_business: businessId,
            isActive: newState,
            transactionType_id: 3
        })
            .then((response) => {
                if (!response.data.success) {
                    setIsZibalActive(!newState);
                    toast.error(response.data.message || "خطا در تغییر وضعیت");
                } else {
                    toast.success(newState ? "درگاه زیبال فعال شد" : "درگاه زیبال غیرفعال شد");
                }
            })
            .catch((err) => {
                setIsZibalActive(!newState);
                console.error(err);
                toast.error("خطا در برقراری ارتباط با سرور");
            });
    };

    const toggleCardToCard = () => {
        if (disable) return;
        const newState = !isCardToCardActive;
        setIsCardToCardActive(newState);

        api.post(`business/payment/gateway/activation`, {
            id_business: businessId,
            isActive: newState,
            transactionType_id: 1
        })
            .then((response) => {
                if (!response.data.success) {
                    setIsCardToCardActive(!newState);
                    toast.error(response.data.message || "خطا در تغییر وضعیت");
                } else {
                    toast.success(newState ? "پرداخت کارت به کارت فعال شد" : "پرداخت کارت به کارت غیرفعال شد");
                }
            })
            .catch((err) => {
                setIsCardToCardActive(!newState);
                console.error(err);
                toast.error("خطا در برقراری ارتباط با سرور");
            });
    };

    const handleAddCard = () => {
        const cleanCard = normalizeCardNumber(cardNumberInput);

        if (!cleanCard || cleanCard.length !== 16) {
            return toast.error("شماره کارت باید ۱۶ رقم باشد");
        }
        if (!isValidCardNumber(cleanCard)) {
            return toast.error("شماره کارت وارد شده نامعتبر است");
        }
        if (cards.length >= 3) {
            return toast.error("حداکثر ۳ کارت مجاز است");
        }
        if (cards.some(c => c.cardNumber === cleanCard)) {
            return toast.error("این کارت تکراری است");
        }

        const newCardsList = [...cards, { cardNumber: cleanCard, visible: true, verify: true }];
        updateCardsOnServer(newCardsList, "کارت افزوده شد");
    };

    const handleDeleteCard = (cardNo) => {
        const newCardsList = cards.filter(c => c.cardNumber !== cardNo);
        updateCardsOnServer(newCardsList, "کارت حذف شد");
    };

    const updateCardsOnServer = (newCardsList, msg) => {
        setDisable(true);
        api.post(`business/card/store`, {
            business_id: businessId,
            transactionType_id: 1,
            config: JSON.stringify(newCardsList)
        })
            .then((response) => {
                setDisable(false);
                if (response.data.success) {
                    setCards(newCardsList);
                    setCardNumberInput("");
                    toast.success(msg);
                } else {
                    toast.error(response.data.message || "خطا در ذخیره اطلاعات");
                }
            })
            .catch(() => {
                setDisable(false);
                toast.error("خطا در برقراری ارتباط با سرور");
            });
    };

    return (
        <div className="payment-gateway-container">

            {/* --- بخش کارت به کارت --- */}
            <div className={`pg-card ${isCardToCardActive ? 'active-border' : ''}`}>
                <div className="pg-header">
                    <div className="pg-title-group">
                        <TfiCreditCard size={22} color="#2196f3" />
                        <span className="pg-title">روش کارت به کارت</span>
                        {isCardToCardActive && <div className="status-badge active">فعال</div>}
                    </div>
                    <div className="pg-toggle" onClick={toggleCardToCard}>
                        {isCardToCardActive ? <BsToggleOn size={35} color="#4caf50" /> : <BsToggleOff size={35} color="#ccc" />}
                    </div>
                </div>

                <div className={`pg-content ${!isCardToCardActive ? 'disabled-area' : ''}`}>
                    <p className="pg-desc">شماره کارت‌های خود را وارد کنید (حداکثر ۳ کارت).</p>
                    <div className="pg-row-input">
                        <input
                            className="pg-input card-input ltr-input"
                            type='number'
                            placeholder='شماره کارت ۱۶ رقمی'
                            value={cardNumberInput}
                            onChange={(e) => { if (e.target.value.length <= 16) setCardNumberInput(e.target.value) }}
                            disabled={!isCardToCardActive || disable}
                        />
                        <button
                            className={`pg-btn-add ${(!isCardToCardActive || disable) ? 'disabled' : ''}`}
                            onClick={(!isCardToCardActive || disable) ? null : handleAddCard}
                        >
                            افزودن
                        </button>
                    </div>

                    <div className="pg-cards-list">
                        {cards.map((card, index) => {
                            const { bankInfo } = BankCardRecognizer(card.cardNumber + "");
                            return (
                                <div key={index} className="pg-card-item">
                                    <div className="card-detail">
                                        <span className="card-label">کارت {index + 1}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {bankInfo && bankInfo.icon && (
                                                <img
                                                    src={process.env.PUBLIC_URL + bankInfo.icon}
                                                    alt="bank-icon"
                                                    style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                                                />
                                            )}
                                            <span className="card-num">{card.cardNumber}</span>
                                        </div>
                                    </div>
                                    <div
                                        className={`card-action ${(!isCardToCardActive || disable) ? 'disabled' : ''}`}
                                        onClick={() => (!isCardToCardActive || disable) ? null : handleDeleteCard(card.cardNumber)}
                                    >
                                        <GoTrash size={18} color={(!isCardToCardActive || disable) ? "#999" : "#f44336"} />
                                    </div>
                                </div>
                            );
                        })}
                        {cards.length === 0 && <div className="pg-empty">هنوز کارتی ثبت نشده است.</div>}
                    </div>
                </div>
            </div>

            {/* --- بخش زرین پال --- */}
            <div className={`pg-card ${isZarinpalActive ? 'active-border' : ''}`} style={{ marginTop: '15px' }}>
                <div className="pg-header">
                    <div className="pg-title-group">
                        <img
                            src={process.env.PUBLIC_URL + zarinpalIcon}
                            alt="zarinpal-icon"
                            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                        />
                        <span className="pg-title">درگاه پرداخت زرین‌پال</span>
                        {isZarinpalActive && <div className="status-badge active">فعال</div>}
                    </div>
                    <div className="pg-toggle" onClick={toggleZarinpal}>
                        {isZarinpalActive ? <BsToggleOn size={35} color="#4caf50" /> : <BsToggleOff size={35} color="#ccc" />}
                    </div>
                </div>

                <div className={`pg-content ${!isZarinpalActive ? 'disabled-area' : ''}`}>
                    <p className="pg-desc">برای اتصال به درگاه پرداخت زرین‌پال، کد مرچنت (Merchant ID) را وارد کنید.</p>
                    <div className="pg-form-group">
                        <input
                            className="pg-input ltr-input"
                            type='text'
                            placeholder='کد مرچنت (Merchant ID)'
                            value={merchantId}
                            onChange={(e) => setMerchantId(e.target.value)}
                            disabled={!isZarinpalActive || disable}
                        />
                    </div>
                    <button
                        className={`pg-btn-save ${(!isZarinpalActive || disable) ? 'disabled' : ''}`}
                        onClick={(!isZarinpalActive || disable) ? null : () => { handleSaveMerchant(2, merchantId, 'زرین‌پال') }}
                    >
                        {disable ? 'در حال پردازش...' : 'ثبت درگاه زرین پال'}
                    </button>
                </div>
            </div>

            {/* --- بخش زیبال --- */}
            <div className={`pg-card ${isZibalActive ? 'active-border' : ''}`} style={{ marginTop: '15px' }}>
                <div className="pg-header">
                    <div className="pg-title-group">
                        <img
                            src={process.env.PUBLIC_URL + zibalIcon}
                            alt="zibal-icon"
                            style={{ width: '24px', height: '24px', objectFit: 'contain' }}
                        />
                        <span className="pg-title">درگاه پرداخت زیبال</span>
                        {isZibalActive && <div className="status-badge active">فعال</div>}
                    </div>
                    <div className="pg-toggle" onClick={toggleZibal}>
                        {isZibalActive ? <BsToggleOn size={35} color="#4caf50" /> : <BsToggleOff size={35} color="#ccc" />}
                    </div>
                </div>

                <div className={`pg-content ${!isZibalActive ? 'disabled-area' : ''}`}>
                    <p className="pg-desc">برای اتصال به درگاه پرداخت زیبال، کد مرچنت (Merchant ID) را وارد کنید.</p>
                    <div className="pg-form-group">
                        <input
                            className="pg-input ltr-input"
                            type='text'
                            placeholder='کد مرچنت زیبال (Merchant ID)'
                            value={zibalMerchantId}
                            onChange={(e) => setZibalMerchantId(e.target.value)}
                            disabled={!isZibalActive || disable}
                        />
                    </div>
                    <button
                        className={`pg-btn-save ${(!isZibalActive || disable) ? 'disabled' : ''}`}
                        onClick={(!isZibalActive || disable) ? null : () => { handleSaveMerchant(3, zibalMerchantId, 'زیبال') }}
                    >
                        {disable ? 'در حال پردازش...' : 'ثبت درگاه زیبال'}
                    </button>
                </div>
            </div>

        </div>
    );
}

export default PaymentGateway;
